import { useState } from "react";

import type { RefillConversation } from "@/lib/refill/conversation";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AIConversation } from "@/components/refill/ai-conversation";
import { ManualModeBanner } from "@/components/refill/manual-mode-banner";
import { SafetyViolationBanner } from "@/components/refill/safety-violation-banner";

// AIPanel is the free-form assistant surface, anchored at the bottom of the
// refill wizard and on the session-detail page. It's a thin, controlled view
// over the ONE shared per-session conversation (useRefillConversation): the
// transcript it shows and the input it sends are the SAME thread the step CTAs
// (analysis, prioritized actions, mixing feedback) append to. So typing here
// genuinely continues that thread — what the grower expects from "the
// conversation input below."
export function AIPanel({
  conv,
  title = "AI assistant",
  placeholder = "Continue this conversation — 'what should I add next?', 'is this safe?', etc.",
}: {
  conv: RefillConversation;
  title?: string;
  placeholder?: string;
}) {
  const [message, setMessage] = useState("");

  function send() {
    if (!message.trim()) return;
    conv.run(message);
    setMessage("");
  }

  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </h2>
        <Button variant="ghost" size="sm" onClick={conv.toggleManual}>
          {conv.manualMode ? "Re-enable AI" : "Manual mode"}
        </Button>
      </header>

      {conv.manualMode ? (
        <>
          <ManualModeBanner reason={conv.manualMode} />
          {conv.manualData && (
            <SafetyViolationBanner violations={conv.manualData.safety_violations} />
          )}
          {conv.manualData && (
            <div className="text-xs text-muted-foreground">
              Predicted final — pH{" "}
              {conv.manualData.trajectory.predicted_final_ph.toFixed(2)}, EC{" "}
              {conv.manualData.trajectory.predicted_final_ec.toFixed(2)}, temp{" "}
              {conv.manualData.trajectory.predicted_final_temp.toFixed(1)}
            </div>
          )}
        </>
      ) : (
        <>
          <AIConversation history={conv.history} />
          <div className="space-y-2">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={placeholder}
              rows={3}
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {conv.usage &&
                  `last turn: ${conv.usage.input_tokens} in / ${conv.usage.output_tokens} out · $${conv.usage.cost_usd.toFixed(4)}`}
              </span>
              <Button
                type="button"
                size="sm"
                disabled={conv.isRunning || !message.trim()}
                onClick={send}
              >
                {conv.isRunning ? "Thinking…" : "Send"}
              </Button>
            </div>
            {conv.error && (
              <p className="text-xs text-destructive">{conv.error}</p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
